import {
  InfiniteData,
  QueryClient,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getRefreshToken,
  queryKeys,
  SystemProblem,
  SystemProblemDashboardResponse,
  SystemProblemEventPayload,
  SystemProblemListResponse,
} from '@vgrubs/components';
import { useEffect } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.NX_API_URL;

type ListData = InfiniteData<SystemProblemListResponse, number>;

/**
 * Whether a list cached under `key` would contain `problem`. The filters are
 * read back out of the key - see `queryKeys.systemProblems.list` for the
 * positions.
 */
const listWants = (key: readonly unknown[], problem: SystemProblem) => {
  const [, , status, locationId, severity, category] = key;
  return (
    (status === null || status === problem.status) &&
    (locationId === null || locationId === problem.locationId) &&
    (severity === null || severity === problem.severity) &&
    (category === null || category === problem.category)
  );
};

/**
 * The Dashboard card, after any event: its count straight away from the
 * event, then the whole card again. Its rows are ordered by severity and its
 * chips and counters move with every problem, so a refetch is the one way to
 * be right - the doc's own advice.
 */
const refreshCard = (queryClient: QueryClient, open?: number) => {
  if (open !== undefined) {
    queryClient.setQueriesData<SystemProblemDashboardResponse>(
      { queryKey: queryKeys.systemProblems.dashboard },
      (card) => card && { ...card, open }
    );
  }
  queryClient.invalidateQueries({
    queryKey: queryKeys.systemProblems.dashboard,
  });
};

/**
 * A new problem goes on top of every loaded list it belongs in - lists are
 * newest first, so the top is exactly where the server would put it. Paging
 * further on may hand the last row of a page back a second time; the list
 * hook drops repeats by id.
 */
const insertProblem = (
  queryClient: QueryClient,
  { problem, open }: SystemProblemEventPayload
) => {
  refreshCard(queryClient, open);

  queryClient
    .getQueriesData<ListData>({ queryKey: queryKeys.systemProblems.listAll })
    .forEach(([key, data]) => {
      if (!data?.pages.length || !listWants(key, problem)) return;
      const [first, ...rest] = data.pages;
      if (first.items.some((row) => row.id === problem.id)) return;
      queryClient.setQueryData<ListData>(key, {
        ...data,
        pages: [
          {
            ...first,
            items: [problem, ...first.items],
            total: first.total + 1,
            open,
          },
          ...rest,
        ],
      });
    });
};

/**
 * A changed problem - resolved by hand, or closed by the system itself
 * (`autoResolved`) - is swapped in place in every list and in its detail. It
 * stays on an Open list as resolved until the next fetch, rather than
 * vanishing from under the reader who was looking at it. The card refetches.
 *
 * Exported for the page's own Resolve, whose answer is the same object.
 */
export const replaceSystemProblem = (
  queryClient: QueryClient,
  problem: SystemProblem,
  open?: number
) => {
  queryClient.setQueriesData<ListData>(
    { queryKey: queryKeys.systemProblems.listAll },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((row) =>
            row.id === problem.id ? problem : row
          ),
        })),
      }
  );
  queryClient.setQueryData(
    queryKeys.systemProblems.detail(problem.id),
    problem
  );
  refreshCard(queryClient, open);
  /* Location Details lists only open problems, so a resolved one leaves it:
     its alerts come back without it rather than showing it as resolved. */
  queryClient.invalidateQueries({
    queryKey: queryKeys.locationInsights.alertsAll,
  });
};

/**
 * Live system problems, while the Dashboard card or the full list is on
 * screen - which is all the backend asks for: there is no badge anywhere else
 * that would need it.
 *
 * Its own socket on `/system-problems`, authenticated with the refresh token
 * like `/conversations`. There are no rooms: every connected admin or support
 * user gets every event, so nothing is emitted after connecting. Any other
 * role is refused at the handshake; both screens are admin and support only.
 */
export const useSystemProblemsSocket = () => {
  const queryClient = useQueryClient();
  const refreshToken = getRefreshToken();
  const active = !!SOCKET_URL && !!refreshToken;

  useEffect(() => {
    if (!active) return undefined;

    const socket = io(SOCKET_URL, {
      path: '/system-problems',
      transports: ['websocket'],
      auth: { authorization: refreshToken },
    });

    socket.on('system_problem_created', (payload: SystemProblemEventPayload) =>
      insertProblem(queryClient, payload)
    );

    socket.on(
      'system_problem_updated',
      ({ problem, open }: SystemProblemEventPayload) =>
        replaceSystemProblem(queryClient, problem, open)
    );

    /*
     * Any connect after the first one has a gap behind it (a network drop, a
     * server restart) and the events of that gap are gone. There are few
     * problems and they arrive slowly, so refetching the lot is cheaper than
     * reasoning about what was missed.
     */
    let connectedBefore = false;
    socket.on('connect', () => {
      if (connectedBefore) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.systemProblems.all,
        });
      }
      connectedBefore = true;
    });

    socket.on('connect_error', (err: Error) => {
      console.error('System problems socket connection error:', err.message);
    });

    return () => {
      socket.disconnect();
    };
    // Keyed on [active, refreshToken] alone: the handlers only touch the
    // query client, which never changes.
  }, [active, refreshToken]);
};
